import { useEffect, useState } from "react";

import { useFormik } from "formik";
import i18next from "i18next";
import { IoMailOutline } from "react-icons/io5";
import * as Yup from "yup";

import { supportGuestRequestCode, supportGuestVerify } from "../../../protocols/rest/support";
import { getSupportErrorKey, isSupportRateLimit, supportErrorText } from "../utils";

import { DotsLoadingComponent, InputComponent } from "../../ui";

import {
  ButtonLoadingCover,
  CardText,
  CardTitle,
  Fields,
  FormError,
  NarrowCard,
  Notice,
  PrimaryButton,
  Row,
  TextButton,
  TextLink,
} from "../style";

const RESEND_SECONDS = 60;
const CODE_LENGTH = 6;

/**
 * Гостевой доступ к заявкам: номер заказа + email брони → код в письме → подтверждение.
 * После verify токен сохраняется в localStorage (protocols/rest/support), SupportProvider переключается в режим гостя.
 */
const SupportAccessForm = ({ presetOrderId = "", presetEmail = "", initialError = null, onVerified = () => null }) => {
  const { t } = i18next;

  const [step, setStep] = useState("request");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(initialError);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState(null);

  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setTimeout(() => setCooldown(value => value - 1), 1000);

    return () => clearTimeout(timer);
  }, [cooldown]);

  const requestCode = async ({ order_id, email }) => {
    setIsLoading(true);
    setFormError(null);

    try {
      const data = await supportGuestRequestCode({ order_id: order_id.trim(), email: email.trim() });

      setMaskedEmail(data?.email || email.trim());
      setStep("code");
      setCode("");
      setCodeError(null);
      setCooldown(RESEND_SECONDS);
    } catch (error) {
      if (getSupportErrorKey(error) === "order_not_exist") {
        setFieldError("order_id", supportErrorText(error));
      } else {
        setFormError(supportErrorText(error));
      }

      // при «слишком часто» не даём сразу повторять
      if (isSupportRateLimit(error)) setCooldown(RESEND_SECONDS);
    } finally {
      setIsLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    order_id: Yup.string().trim().required(t("required")),
    email: Yup.string().trim().email(t("enter_valid_mail")).required(t("required")),
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldError } = useFormik({
    initialValues: { order_id: presetOrderId, email: presetEmail },
    validationSchema,
    onSubmit: requestCode,
    validateOnBlur: true,
  });

  const verify = async () => {
    if (code.length !== CODE_LENGTH) {
      setCodeError(t("required"));
      return;
    }

    setIsLoading(true);
    setFormError(null);
    setCodeError(null);

    try {
      const data = await supportGuestVerify({ order_id: values.order_id.trim(), code });
      onVerified(data);
    } catch (error) {
      if (getSupportErrorKey(error) === "support_code_invalid") {
        setCodeError(supportErrorText(error));
      } else {
        setFormError(supportErrorText(error));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const submitOnEnter = callback => e => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      callback();
    }
  };

  const loadingCover = isLoading && (
    <ButtonLoadingCover>
      <DotsLoadingComponent color="#fff" />
    </ButtonLoadingCover>
  );

  return (
    <NarrowCard>
      <CardTitle>{t("support_access_title")}</CardTitle>
      <CardText>{t("support_access_subtitle")}</CardText>

      {step === "request" ? (
        <>
          <Notice $variant="info">
            <IoMailOutline />
            <span style={{ flex: 1 }}>{t("support_access_hint")}</span>
          </Notice>

          <Fields>
            <Row>
              <InputComponent
                value={values.order_id}
                onChange={handleChange("order_id")}
                onBlur={handleBlur("order_id")}
                onKeyDown={submitOnEnter(handleSubmit)}
                label={t("support_order_number")}
                placeholder={t("support_enter_order_number")}
                touched={touched.order_id}
                error={errors.order_id}
                required
              />
              <InputComponent
                value={values.email}
                onChange={handleChange("email")}
                onBlur={handleBlur("email")}
                onKeyDown={submitOnEnter(handleSubmit)}
                type="email"
                label={t("support_access_email")}
                placeholder="example@mail.com"
                touched={touched.email}
                error={errors.email}
                required
              />
            </Row>
          </Fields>

          {formError && <FormError>{formError}</FormError>}

          <PrimaryButton type="button" onClick={handleSubmit} disabled={isLoading || cooldown > 0}>
            {cooldown > 0 ? t("support_code_resend_in", { seconds: cooldown }) : t("support_access_send_code")}
            {loadingCover}
          </PrimaryButton>
        </>
      ) : (
        <>
          <Notice $variant="success">
            <IoMailOutline />
            <span style={{ flex: 1 }}>{t("support_code_sent", { email: maskedEmail })}</span>
          </Notice>

          <InputComponent
            value={code}
            onChange={value => setCode(String(value).replace(/\D/g, "").slice(0, CODE_LENGTH))}
            onKeyDown={submitOnEnter(verify)}
            label={t("support_code_label")}
            placeholder={t("support_code_placeholder")}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            touched={!!codeError}
            error={codeError}
            required
            autoFocus
          />

          {formError && <FormError>{formError}</FormError>}

          <PrimaryButton type="button" onClick={verify} disabled={isLoading}>
            {t("support_code_confirm")}
            {loadingCover}
          </PrimaryButton>

          <Row>
            <TextButton type="button" onClick={() => requestCode(values)} disabled={isLoading || cooldown > 0}>
              {cooldown > 0 ? t("support_code_resend_in", { seconds: cooldown }) : t("support_code_resend")}
            </TextButton>
            <TextButton
              type="button"
              onClick={() => {
                setStep("request");
                setFormError(null);
              }}
              style={{ justifySelf: "end" }}
            >
              {t("support_change_order")}
            </TextButton>
          </Row>
        </>
      )}

      <CardText>
        {t("support_have_account")}{" "}
        <TextLink to={`/auth?redirect=${encodeURIComponent("/support")}`} style={{ display: "inline-flex" }}>
          {t("support_login")}
        </TextLink>
      </CardText>
    </NarrowCard>
  );
};

export default SupportAccessForm;
